import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router'
import { AuthenticationService } from '../../../../../framework/authentication/services/authentication.service'
import { Injectable } from '@angular/core'
import { combineLatest, of, ReplaySubject } from 'rxjs'
import { ConfigurationRouteService } from '../services/configurationRoute.service'
import { ConfigurationIdResolver } from '../resolvers/configurationId.resolver'
import { distinctUntilChanged, filter, map, mergeMap } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

@UntilDestroy()
@Injectable()
export class NeedloginGuard implements CanActivate {
    path$: ReplaySubject<string> = new ReplaySubject()

    constructor(
        private authenticationService: AuthenticationService,
        private router: Router,
        private configurationIdResolver: ConfigurationIdResolver,
        private configurationRouteService: ConfigurationRouteService
    ) {
        // detect if user is at an unavailable step within the checkout, redirect if so
        configurationIdResolver.configurationId$
            .pipe(
                untilDestroyed(this),
                distinctUntilChanged(),

                // combine current configurationId, routes of that id and the currently navigated path
                mergeMap(configurationId =>
                    combineLatest([
                        of(configurationId),
                        this.configurationRouteService.getConfigurationRoutes(configurationId),
                        this.path$,
                    ])
                ),

                // match known route to current path
                map(([configurationId, routes, currentPath]) => ({
                    configurationId,
                    route: routes.find(({ path }) => path === currentPath),
                })),

                // only take action if route is unknown or unavailable
                filter(({ route }) => route === null || route === undefined || !route.isAvailable)
            )
            .subscribe(({ configurationId }) => {
                // the user is currently routed to an unavailable route, redirect to the best option
                this.configurationRouteService.routeToAvailableRoute(configurationId)
            })
    }

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        const [path] = state.url.split('?')
        this.path$.next(path)

        let result = true

        if (
            route.data.hasOwnProperty('skipLogin') &&
            route.data.skipLogin !== true &&
            !this.authenticationService.isLoggedIn
        ) {
            result = false
            console.warn('you need to be logged in for this checkout step to open')
            this.router.navigate([state.url.split('/').slice(0, -1).join('/')])
        }

        return result
    }
}
